import {Injectable} from '@angular/core';
import {
  ActiveStatus,
  AuditLogPage,
  AuditLogsService,
  Country,
  SortOrderEnum,
  VenueAccessType,
  VenueGet,
  VenuePost,
  VenuePut,
  VenueService as API,
  VenueType
} from '../../../generated/sales-terminal-client';
import {LanguageService} from '../../language/language.service';
import {Observable, tap} from 'rxjs';
import {SnackService} from '../../snack/snack.service';
import {PageableResult, PageableSearch, ScrollLoadService} from '../../../components/scroll-load-component/scroll-load.component';
import {ScrollLoad} from '../../../components/scroll-load-component/ScrollLoad';
import {find} from 'lodash-es';
import {CountryService} from '../../country/country.service';
import {TranslateService} from "@ngx-translate/core";

export interface VenuePageSearch extends PageableSearch {
  status?: ActiveStatus;
  query?: string;
  country?: string;
  type?: VenueType;
  placeId?: string;
  sortOrder?: SortOrderEnum;
  accessType?: VenueAccessType;
}

@Injectable({
  providedIn: 'root'
})
export class SelfServiceVenueService implements ScrollLoadService<VenueGet> {

  constructor(private api: API,
              private languageService: LanguageService,
              private snackService: SnackService,
              private countryService: CountryService,
              private auditLogsService: AuditLogsService,
              private translateService: TranslateService) {}

  public search(data: VenuePageSearch): Observable<PageableResult<VenueGet>> {
    return this.api.searchVenues(data.status, data.query, data.country, data.type, data.accessType, data.placeId, data.page, data.pageSize, data.sortOrder).pipe(
      tap({error: (e) => this.snackService.showBackendError(e)})
    );
  }
  public displayScrollLoadOption(option: VenueGet): string {
    return option.name + '&nbsp;<span class="text-muted">(' + this.translateService.instant('general.api_venues_type_' + option.type.toLowerCase()) + ')</span>';
  }
  public displayScrollLoadSelection(selection: VenueGet): string | undefined {
    return selection?.name || undefined;
  }
  public getScrollLoad(pageSize?: number, idKey?: string): ScrollLoad<VenueGet> {
    return new ScrollLoad<VenueGet>(pageSize || 20, idKey || 'id');
  }
  public setActiveVenuesQuery(query: string): VenuePageSearch {
    return {status: ActiveStatus.Active, query: query};
  }

  public formatAddressForMap(venue: VenueGet): string {
    return venue.address +
      (venue.city ? (', ' + venue.city) : '') +
      (venue.county ? (', ' + venue.county) : '') +
      (venue.address ? (', ' + this.getCountry(venue.countryCode)?.name) : this.getCountry(venue.countryCode)?.name);
  }
  public getCountry(code: string): Country {
    return find(this.countryService.countries, { code: code });
  }

  public get(id: number): Observable<VenueGet> {
    return this.api.getVenue(id).pipe(
      tap({error: (e) => this.snackService.showBackendError(e)})
    );
  }
  public create(data: VenuePost): Observable<VenueGet> {
    return this.api.createVenue(data).pipe(
      tap({error: (e) => this.snackService.showBackendError(e)})
    );
  }
  public update(id: number, data: VenuePut): Observable<VenueGet> {
    return this.api.updateVenue(id, data).pipe(
      tap({error: (e) => this.snackService.showBackendError(e)})
    );
  }
  public delete(id: number): Observable<any> {
    return this.api.deleteVenue(id).pipe(
      tap({error: (e) => this.snackService.showBackendError(e)})
    );
  }
  public getAuditLogs(venueId: number, page: number, pageSize: number): Observable<AuditLogPage> {
    return this.auditLogsService.getVenueAuditLogsByVenueId(venueId, page, pageSize).pipe(
      tap({error: (e) => this.snackService.showBackendError(e)})
    );
  }
}
